import React from 'react';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, recovering: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo?.componentStack);

    // Auto-recover from chunk load errors (stale PWA cache)
    const errorMsg = error?.message || error?.toString() || '';
    if (errorMsg.includes('Loading chunk') || errorMsg.includes('Loading CSS chunk') ||
        errorMsg.includes('ChunkLoadError') || errorMsg.includes('Unexpected token')) {
      this.handleClearCacheAndReload();
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleClearCacheAndReload = async () => {
    try {
      // Unregister all service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const reg of registrations) {
          await reg.unregister();
        }
      }
      // Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        for (const name of cacheNames) {
          await caches.delete(name);
        }
      }
    } catch (e) {
      console.warn('[ErrorBoundary] Cache clear error:', e);
    }
    // Force full reload from network
    window.location.reload(true);
  };

  render() {
    if (this.state.hasError) {
      const { fallbackTitle = 'Something went wrong', onGoBack } = this.props;
      
      return (
        <div className="flex items-center justify-center min-h-[400px] p-4">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mb-2">
                <AlertCircle className="h-6 w-6 text-destructive" />
              </div>
              <CardTitle>{fallbackTitle}</CardTitle>
              <CardDescription>
                An error occurred while loading this page. This has been logged for investigation.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {this.state.error && (
                <div className="p-3 bg-muted rounded-lg text-sm font-mono text-muted-foreground overflow-auto max-h-32">
                  {this.state.error.toString()}
                </div>
              )}
              <div className="flex gap-2 justify-center flex-wrap">
                <Button variant="outline" onClick={this.handleRetry}>
                  Try Again
                </Button>
                <Button variant="outline" onClick={this.handleClearCacheAndReload} className="text-amber-700 border-amber-300 hover:bg-amber-50">
                  Clear Cache & Reload
                </Button>
                {onGoBack && (
                  <Button onClick={onGoBack}>
                    Go Back
                  </Button>
                )}
              </div>
              <p className="text-xs text-center text-muted-foreground">
                If the error persists, try "Clear Cache & Reload" to reset the app.
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
